import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../utils/apiUtils/apiUtils";
import { sessionExpired } from "../../utils/constants";
import { extractApiErrorCode, extractApiErrorMessage } from "../../utils/apiUtils/apiErrors";

export const apiRequest = createAsyncThunk(
  "api/request",
  async ({ method, url, payload = null, headers = undefined }, { rejectWithValue }) => {
    try {
      let response = "";
      const requestConfig = {
        method,
        url,
        data: payload,
        ...(headers ? { headers } : {}),
      };
      const isBlobResponse =
        url.includes("generatepdf") ||
        (url.includes("audit") &&
          (url.includes("pdf") || url.includes("csv"))) ||
        (payload != null && payload?.exportCsv === true);

      if (isBlobResponse) {
        response = await api({
          ...requestConfig,
          responseType: "blob",
        });
      } else {
        response = await api(requestConfig);
      }
      if (response?.data?.isSuccess) {
        return response.data;
      } else if (isBlobResponse && response?.status === 200) {
        return response.data;
      } else {
        return rejectWithValue({
          message: extractApiErrorMessage(response?.data, response?.status),
          code: extractApiErrorCode(response?.data),
          status: response?.status ?? null,
        });
      }
    } catch (error) {
      const errorMsg = extractApiErrorMessage(
        error?.response?.data,
        error?.response?.status
      );
      console.error("API call error::", error, errorMsg);
      return rejectWithValue({
        message: errorMsg,
        code: extractApiErrorCode(error?.response?.data),
        status: error?.response?.status ?? null,
      });
    }
  }
);

const apiSlice = createSlice({
  name: "api",
  initialState: {
    loading: false,
    error: null,
    errorCode: null,
    errorStatus: null,
  },
  reducers: {
    clearApiError(state) {
      state.error = null;
      state.errorCode = null;
      state.errorStatus = null;
    },
    setApiError(state, action) {
      state.error = action?.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(apiRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.errorCode = null;
        state.errorStatus = null;
      })
      .addCase(apiRequest.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(apiRequest.rejected, (state, action) => {
        state.loading = false;
        // Kept a string so every existing reader of state.api.error still works;
        // the code and status travel alongside for callers that branch on them.
        const payload = action.payload;
        const message = typeof payload === "string" ? payload : payload?.message;
        if (state?.error !== sessionExpired) {
          state.error = message ?? null;
        }
        state.errorCode = typeof payload === "string" ? null : payload?.code ?? null;
        state.errorStatus = typeof payload === "string" ? null : payload?.status ?? null;
      });
  },
});

export const { clearApiError, setApiError } = apiSlice.actions;

export default apiSlice.reducer;
